import core_isObject from 'lodash/isObject';
import core_isPlainObject from 'lodash/isPlainObject';
import core_isEmpty from 'lodash/isEmpty';
import core_isRegExp from 'lodash/isRegExp';
import core_isArray from 'lodash/isArray';
import core_isFunction from 'lodash/isFunction';
import core_isDate from 'lodash/isDate';
import core_isString from 'lodash/isString';
import core_isBoolean from 'lodash/isBoolean';
import core_isEqual from 'lodash/isEqual';
import core_isNumber from 'lodash/isNumber';
import core_toNumber from 'lodash/toNumber';
import core_cloneDeep from 'lodash/cloneDeep';
import core_replace from 'lodash/replace';
import core_camelCase from 'lodash/camelCase';
import core_get from 'lodash/get';
import { v4 as uuidv4 } from 'uuid';
import DeviceHelper from './device';
import RoutingHelper from './routing';
import ValidationManager from '../services/validation';
// #region *Konstanten
const PLACEHOLDER_REGEXP = /\{([0-9a-zA-Z]+)\}/g;
// #endregion
export default class UtilsClass {
    // #region Properties
    device = new DeviceHelper();
    routing = new RoutingHelper();
    // #endregion
    // #region Debug
    logInfo(name = 'Core', ...message) {
        if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
            message.unshift(`[${name}]`);
            console.info.apply(this, message);
        }
    }
    logWarning(name = 'Core', ...message) {
        if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
            message.unshift(`[${name}]`);
            console.warn.apply(this, message);
        }
    }
    logError(name = 'Core', ...message) {
        if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
            message.unshift(`[${name}]`);
            console.error.apply(this, message);
        }
    }
    // #endregion
    // #region Tools
    isPromise(o) {
        return (typeof o === 'object' || typeof o === 'function') && typeof o.then === 'function';
    }
    isNullOrEmpty(value) {
        if (this.isNull(value)) {
            return true;
        }
        if (this.isUndefined(value)) {
            return true;
        }
        if (this.isEmpty(value)) {
            return true;
        }
        return false;
    }
    isNull(value) {
        if (value === null) {
            return true;
        }
        if (value === undefined) {
            return true;
        }
        return false;
    }
    isEmpty(value) {
        return core_isEmpty(value);
    }
    notEmpty(value) {
        return core_isEmpty(value) == false;
    }
    isString(value) {
        return core_isString(value);
    }
    isObject(value) {
        return core_isObject(value);
    }
    isPlainObject(value) {
        return core_isPlainObject(value);
    }
    isRegExp(value) {
        return core_isRegExp(value);
    }
    isArray(value) {
        return core_isArray(value);
    }
    isFunction(value) {
        return core_isFunction(value);
    }
    isDate(value) {
        return core_isDate(value);
    }
    isBoolean(value) {
        return core_isBoolean(value);
    }
    isEqual(value, other) {
        return core_isEqual(value, other);
    }
    isNumber(value) {
        return core_isNumber(value);
    }
    isUndefined(value) {
        return typeof value === 'undefined';
    }
    concat(separator) {
        var args1 = Array.prototype.slice.call(arguments, 1);
        var args2 = args1.filter(function (el) {
            return !utils.isNullOrEmpty(el);
        });
        return args2.join(separator);
    }
    // #endregion
    // #region Conversion
    toNumber(value) {
        return core_toNumber(value);
    }
    trimChar(string, charToRemove) {
        if (!string) {
            return string;
        }
        while (string.charAt(0) == charToRemove) {
            string = string.substring(1);
        }
        while (string.charAt(string.length - 1) == charToRemove) {
            string = string.substring(0, string.length - 1);
        }
        return string;
    }
    // #endregion
    // #region Window
    getScrollbarHeight(element) {
        if (!(element instanceof HTMLElement)) {
            this.logWarning('DOM Utilities', 'The provided element needs to be an instance of "HTMLElement".', element);
            return 0;
        }
        return (element.offsetHeight - element.clientHeight);
    }
    getScrollbarWidth(element) {
        if (!(element instanceof HTMLElement)) {
            this.logWarning('DOM Utilities', 'The provided element needs to be an instance of "HTMLElement".', element);
            return 0;
        }
        return (element.offsetWidth - element.clientWidth);
    }
    isPositionInPolygon(x, y, polygon) {
        // Inspired by https://github.com/substack/point-in-polygon/blob/master/index.js
        let inside = false;
        // eslint-disable-next-line no-plusplus
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i][0];
            const yi = polygon[i][1];
            const xj = polygon[j][0];
            const yj = polygon[j][1];
            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect)
                inside = !inside;
        }
        return inside;
    }
    // #endregion
    // #region Clipboard
    copyToClipboard(stringToCopy) {
        const tempTextArea = document.createElement('textarea');
        tempTextArea.value = stringToCopy;
        document.body.appendChild(tempTextArea);
        tempTextArea.select();
        document.execCommand('copy');
        document.body.removeChild(tempTextArea);
    }
    // #endregion
    // #region Formating
    format(...args) {
        if (args.length === 0) {
            return "";
        }
        else if (args.length === 1) {
            return args[0];
        }
        else {
            var args2 = args;
            var message = args2[0];
            return message.replace(PLACEHOLDER_REGEXP, function (match, placeholder, index) {
                if (message[index - 1] === "{" && message[index + match.length] === "}") {
                    return placeholder; // deal with the escaped brackets
                }
                else {
                    var i = parseInt(placeholder);
                    var result = args2[i + 1];
                    if (result === null || result === undefined) {
                        return "";
                    }
                    else {
                        return result;
                    }
                }
            });
        }
    }
    leftWithEllipsis(str, maxLength, { side = "end", ellipsis = "..." } = {}) {
        if (str.length > maxLength) {
            switch (side) {
                case "start":
                    return ellipsis + str.slice(-(maxLength - ellipsis.length));
                case "end":
                default:
                    return str.slice(0, maxLength - ellipsis.length) + ellipsis;
            }
        }
        return str;
    }
    formatNumberWithDcp(value, precision) {
        return new Intl.NumberFormat(navigator.language, { style: 'decimal', minimumFractionDigits: precision, maximumFractionDigits: precision }).format(value);
    }
    formatFileSize(bytes, si = false, dp = 1) {
        const thresh = 1024;
        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }
        const units = si
            ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
            : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
        let u = -1;
        const r = 10 ** dp;
        do {
            bytes /= thresh;
            ++u;
        } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
        return this.formatNumberWithDcp(bytes, dp) + ' ' + units[u];
    }
    formatDate(value, mode = "date-time") {
        try {
            if (!value) {
                return '';
            }
            if (this.isString(value)) {
                value = Date.parse(value);
            }
            if (mode === 'date') {
                return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium' }).format(value);
            }
            else if (mode === 'date-time') {
                return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium', timeStyle: 'medium' }).format(value);
            }
            else if (mode === 'date-shorttime') {
                return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium', timeStyle: 'short' }).format(value);
            }
            else {
                return '';
            }
        }
        catch {
            this.logError('formatDate', value, mode);
            return '';
        }
    }
    //#endregion
    // #region ID
    createId() {
        return uuidv4();
    }
    createElementId() {
        // desired length of Id
        var idStrLen = 32;
        // always start with a letter -- base 36 makes for a nice shortcut
        var idStr = (Math.floor((Math.random() * 25)) + 10).toString(36) + "_";
        // add a timestamp in milliseconds (base 36 again) as the base
        idStr += (new Date()).getTime().toString(36) + "_";
        // similar to above, complete the Id using random, alphanumeric characters
        do {
            idStr += (Math.floor((Math.random() * 35))).toString(36);
        } while (idStr.length < idStrLen);
        return (idStr);
    }
    // #endregion
    // #region Object
    getObjectValue(object, path) {
        try {
            return core_get(object, path);
        }
        catch (e) {
            //set
            utils.logWarning("utils.getObjectValue", 'Not a valid path for ' + path, object);
            return null;
        }
    }
    setObjectValue(object, path, value) {
        let tPath;
        if (typeof path === 'string') {
            tPath = path.split('.');
        }
        else {
            tPath = path;
        }
        try {
            for (let i = 0; i < tPath.length - 1; i++) {
                if (/^[0-9]+$/.test(tPath[i]) && object[tPath[i]] === undefined) {
                    continue;
                }
                if (!object[tPath[i]]) {
                    object[tPath[i]] = {};
                }
                object = object[tPath[i]];
            }
            object[tPath[tPath.length - 1]] = value;
        }
        catch (e) {
            utils.logWarning("utils.setObjectValue", 'Not a valid path for ' + tPath, e);
        }
    }
    cloneDeep(object) {
        return core_cloneDeep(object);
    }
    buildFromProps(obj, props) {
        const res = {};
        props.forEach((prop) => this.defineDescriptor(obj, res, prop));
        return res;
    }
    mergeObject(objs) {
        const res = {};
        objs.forEach((obj) => {
            obj &&
                Object.getOwnPropertyNames(obj).forEach(name => this.defineDescriptor(obj, res, name));
        });
        return res;
    }
    mergeDeep(target, ...sources) {
        if (!sources.length)
            return target;
        const source = sources.shift();
        if (this.isObject(target) && this.isObject(source)) {
            for (const key in source) {
                if (this.isObject(source[key])) {
                    if (!target[key])
                        Object.assign(target, { [key]: {} });
                    this.mergeDeep(target[key], source[key]);
                }
                else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }
        return this.mergeDeep(target, ...sources);
    }
    defineDescriptor(src, dest, name) {
        if (!dest.hasOwnProperty(name)) {
            const descriptor = Object.getOwnPropertyDescriptor(src, name);
            Object.defineProperty(dest, name, descriptor);
        }
    }
    // #endregion
    // #region Axios
    getAxiosError(e) {
        return {
            name: this.getAxiosErrorCode(e),
            message: this.getAxiosErrorMessage(e)
        };
    }
    getAxiosErrorCode(e) {
        if (!this.isNullOrEmpty(e?.response?.data?.error)) {
            return e.response.data.error;
        }
        else {
            return "";
        }
    }
    getAxiosErrorMessage(e) {
        if (utils.isNullOrEmpty(e)) {
            return "";
        }
        if (!this.isNullOrEmpty(e?.response?.data?.message)) {
            return e.response.data.message;
        }
        else if (!this.isNullOrEmpty(e?.response?.data?.error_description)) {
            return e?.response?.data?.error_description;
        }
        else if (e?.response?.data.errors) {
            if (utils.isObject(e?.response?.data.errors)) {
                let tError = "";
                let tData = e?.response?.data.errors;
                Object.keys(tData).forEach(e => {
                    if (utils.isArray(tData[e])) {
                        tData[e].forEach((f) => {
                            if (tError) {
                                tError = tError + ", ";
                            }
                            tError = tError + f;
                        });
                    }
                    else {
                        if (tError) {
                            tError = tError + ", ";
                        }
                        tError = tError + tData[e];
                    }
                });
                return tError;
            }
            else {
                return e?.response?.data.errors;
            }
        }
        else if (e?.response?.data && this.isObject(e?.response?.data)) {
            if (e?.response?.data.title) {
                return e?.response?.data.title;
            }
            else {
                return "Unknown Error, please contact the service-desk.";
            }
        }
        else if (e?.response?.data) {
            return e?.response?.data;
        }
        else if (!this.isNullOrEmpty(e.message)) {
            return e.message;
        }
        else {
            return "Unknown Error, please contact the service-desk.";
        }
    }
    // #endregion
    // #region Validator
    getValidationManager(component) {
        return new ValidationManager(component);
    }
    findForm(object) {
        if (!object) {
            return undefined;
        }
        if (object.$form) {
            return object.$form;
        }
        return this.findForm(object.$parent);
    }
    findVueComponent(sourceComponent, componentName) {
        let component = null;
        let parent = sourceComponent.$parent;
        let isarray = Array.isArray(componentName);
        while (parent && !component) {
            if (isarray) {
                if (componentName.some(e => e === parent.$options.name)) {
                    return parent;
                }
            }
            else {
                if (parent.$options.name === componentName) {
                    return parent;
                }
            }
            parent = parent.$parent;
        }
        return component;
    }
    // #endregion
    // #region CDN
    mediaUrlWithSize(url, width = 0, height = 0) {
        if (this.isNullOrEmpty(url)) {
            return url;
        }
        if (width === 0 && height === 0) {
            return url;
        }
        //New
        var tUrl = new URL(url);
        if (width > 0) {
            tUrl.searchParams.set("width", width.toString());
        }
        if (height > 0) {
            tUrl.searchParams.set("height", height.toString());
        }
        return tUrl.toString();
    }
    // #endregion
    // #region Cookies
    getCookieValue(cname) {
        let name = cname + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    }
    // #endregion
    // #region text
    toCamelCase(string) {
        return core_camelCase(string);
    }
    replace(string, pattern, replacement) {
        return core_replace(string, pattern, replacement);
    }
    evalParams(text, object) {
        const string = text.replace(/{\w+}/g, placeholder => object[placeholder.substring(1, placeholder.length - 1)] || '');
        return string;
    }
    // #endregion
    // #region Files
    getPathInfo(path) {
        let s = path.match(/(.*?[\\/:])?(([^\\/:]*?)(\.[^\\/.]+?)?)(?:[?#].*)?$/);
        if (!s) {
            return { path: "", file: "", name: "", extension: "" };
        }
        else {
            return { path: s[1], file: s[2], name: s[3], extension: s[4] };
        }
    }
    // #endregion
    // #region Eval
    evalExpression(item, exp) {
        var tContext = '';
        Object.keys(item).forEach(e => {
            tContext = tContext + 'let ' + e + ' = item.' + e + ';';
        });
        return eval(tContext + " " + exp);
    }
    // #endregion
    // #region Array
    groupBy(array, key) {
        const result = {};
        array.forEach(item => {
            let tValue = utils.getObjectValue(item, key);
            if (!result[tValue]) {
                result[tValue] = [];
            }
            result[tValue].push(item);
        });
        return result;
    }
}
